import ComponentListDataProvider, {ComponentListDataProviderProps} from "./ComponentListDataProvider";
import {RequestParams} from "../Types/RequestParams";
import {ComponentConfigType} from "../../../../../utils/ComponentConfigType";
import AbortablePromise from "../../../../../utils/promise/AbortablePromise";
import {Items} from "../Types/Items";
import DataProviderComp from "./DataProviderComp";
import {postData} from "../../../../../utils/request";
import {set} from "../../../../../utils/dotutils";

export type ListFieldDataProviderProps = ComponentListDataProviderProps & {
    name?: string,
    listfield?: string,
    parentid?: string|number,
    data?: object,
    componentListFieldConfig: ComponentConfigType,
    listfieldtype?: string,
}

export type ListFieldRequestParams = RequestParams & {
    parentcomponent: string,
    parentid: string|number,
    listfield: string,
}

export default class ListFieldDataProvider<Props extends ListFieldDataProviderProps, State> extends ComponentListDataProvider<Props, State> {

    static defaultProps = {
        selectorFunction: ({item, items, selection, props, selectionType = "index"}) => {
            switch (selectionType) {
                case "selectable":
                    return items.some((selected) => {
                        if (selected === item) return true;
                        if (selected instanceof Object && item instanceof Object) {
                            return selected[props.componentListFieldConfig.idfield] + "" === "" + item[props.componentListFieldConfig.idfield];
                        } else if (selected !== undefined && item !== undefined) {
                            return selected + "" === "" + item;
                        } else if (selected instanceof Object && !(item instanceof Object)) {
                            return selected[props.componentListFieldConfig.idfield] + "" === "" + item;
                        } else if (!(selected instanceof Object) && item instanceof Object) {
                            return selected + "" === "" + item[props.componentListFieldConfig.idfield];
                        }
                    });
                case "index":
                    return selection.findIndex((selected) => {
                        if (selected === item) return true;
                        if (selected instanceof Object && item instanceof Object) {
                            return selected[props.componentListFieldConfig.idfield] + "" === "" + item[props.componentListFieldConfig.idfield];
                        } else if (selected !== undefined && item !== undefined) {
                            return selected + "" === "" + item;
                        } else if (selected instanceof Object && !(item instanceof Object)) {
                            return selected[props.componentListFieldConfig.idfield] + "" === "" + item;
                        } else if (!(selected instanceof Object) && item instanceof Object) {
                            return selected + "" === "" + item[props.componentListFieldConfig.idfield];
                        }
                    });
                case "id":
                    let selected = selection.find((selected) => {
                        if (selected === item) return true;
                        if (selected instanceof Object && item instanceof Object) {
                            return selected[props.componentListFieldConfig.idfield] + "" === "" + item[props.componentListFieldConfig.idfield];
                        } else if (selected !== undefined && item !== undefined) {
                            return selected + "" === "" + item;
                        } else if (selected instanceof Object && !(item instanceof Object)) {
                            return selected[props.componentListFieldConfig.idfield] + "" === "" + item;
                        } else if (!(selected instanceof Object) && item instanceof Object) {
                            return selected + "" === "" + item[props.componentListFieldConfig.idfield];
                        }
                    });
                    return selected && selected instanceof Object ? selected[props.componentListFieldConfig.idfield] : selected;
            }
        },
    }

    constructor(props: Props) {
        super(props);
        this.generateRequest = this.generateRequest.bind(this);
        this.addItems = this.addItems.bind(this);
        this.editItems = this.editItems.bind(this);
        this.removeItems = this.removeItems.bind(this);
        this.rowKeyField = props.componentListFieldConfig.idfield ?? this.rowKeyField;
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (this.props.componentListFieldConfig.idfield !== prevProps.componentListFieldConfig.idfield) {
            this.rowKeyField = this.props.componentListFieldConfig.idfield ?? this.rowKeyField;
        }
    }

    parseView(props: Props): object {
        let view: Record<string, any> = {};
        if (props.view) {
            if (props.view instanceof Function) {
                view = props.view(props);
            }
            if (typeof props.view === "string") {
                if (props.componentConfig.views && props.componentConfig.views[props.view]) {
                    view = props.componentConfig.views[props.view];
                } else {
                    view = {};
                    //console.warn(`No ${props.view} view definition in ${props.componentConfig.componentname}!`);
                }
            } else if (Array.isArray(props.view)) {
                props.view.map((v) => set(view, v, ""));
            }
        }
        if (!view[props.componentListFieldConfig.idfield]) {
            set(view, props.componentListFieldConfig.idfield, "");
        }
        view = {...view};
        if (props.groupField) {
            set(view, props.groupField, "");
        }
        props.getFields().map((column) => set(view, column.props.field, ""));
        return view;
    }

    async addItems(items: Items): Promise<any> {
        items = await DataProviderComp.prototype.addItems(items);
        let ret: void|AbortablePromise<any[]>|Items = items;
        let refresh = false;
        if (this.props.addItems) {
            ret = await this.props.addItems(items, this.props);
        }
        if (ret?.map instanceof Function) {
            await Promise.all(ret.map(async (item, idx) => {
                if (item) {
                    const parentid = this.props?.data?.[this.props.componentConfig.idfield] || this.props.parentid;
                    if (item[this.props.componentConfig.componentname + "_id"] === undefined) {
                        item[this.props.componentConfig.componentname + "_id"] = parentid;
                    }
                    let actionparams = {
                        parentcomponent: this.props.componentConfig.componentname,
                        listfield: this.props.name,
                        parentid,
                        action: "componentlist." + (item[this.props.componentListFieldConfig.idfield] ? "updateItems" : "addItemsToList"),
                        items: [item]
                    };
                    await postData(actionparams);
                }
            }).filter(Boolean));
            refresh = true;
        }
        if (this.props.onItemsChanged) {
            this.props.onItemsChanged();
        }
        if (refresh) {
            this.props.refresh({silent: true});
        }
    }

    async editItems(items: Items): Promise<any> {
        items = await DataProviderComp.prototype.editItems(items);
        let ret: void|AbortablePromise<any[]>|Items = items;
        let refresh = false;
        if (this.props.editItems) {
            ret = await this.props.editItems(items, this.props);
        }
        if (ret?.map instanceof Function) {
            await Promise.all(ret.map(async (item, idx) => {
                if (item) {
                    const parentid = this.props?.data?.[this.props.componentConfig.idfield] || this.props.parentid;
                    if (item[this.props.componentConfig.componentname + "_id"] === undefined) {
                        item[this.props.componentConfig.componentname + "_id"] = parentid;
                    }
                    let actionparams = {
                        parentcomponent: this.props.componentConfig.componentname,
                        listfield: this.props.name,
                        parentid,
                        action: "componentlist." + (item[this.props.componentListFieldConfig.idfield] ? "updateItems" : "addItemsToList"),
                        items: [item]
                    };
                    await postData(actionparams);
                }
            }).filter(Boolean));
            refresh = true;
        }
        if (this.props.onItemsChanged) {
            this.props.onItemsChanged();
        }
        if (refresh) {
            this.props.refresh({silent: true});
        }
    }

    async removeItems(items: Items): Promise<any> {
        items = await DataProviderComp.prototype.removeItems(items);
        let ret: void|AbortablePromise<any[]>|Items = items;
        let refresh = false;
        if (this.props.removeItems) {
            ret = await this.props.removeItems(items, this.props);
        }
        if (ret?.map instanceof Function) {
            await Promise.all(ret.map(async (item, idx) => {
                if (item) {
                    const parentid = this.props?.data?.[this.props.componentConfig.idfield] || this.props.parentid;
                    if (item[this.props.componentConfig.componentname + "_id"] === undefined) {
                        item[this.props.componentConfig.componentname + "_id"] = parentid;
                    }
                    let actionparams = {
                        parentcomponent: this.props.componentConfig.componentname,
                        listfield: this.props.name,
                        parentid,
                        action: "componentlist.removeItemsFromList",
                        items: [item]
                    };
                    await postData(actionparams);
                }
            }).filter(Boolean));
            refresh = true;
        }
        if (this.props.onItemsChanged) {
            this.props.onItemsChanged();
        }
        if (refresh) {
            this.props.refresh({silent: true});
        }
    }

    generateRequest(props: Props, state: State): RequestParams {
        const request: ListFieldRequestParams = {
            ...super.generateRequest(props, state),
            parentcomponent: props.componentConfig.componentname,
            parentid: props.parentid || props.data[props.componentConfig.idfield],
            listfield: props.name,
        };
        delete request.component;
        return request;
    }

}